import { useState, type FormEvent } from 'react';
import { requestAccess } from '../lib/api';

type FormState =
  | { status: 'idle' }
  | { status: 'submitting' }
  | { status: 'success'; email: string }
  | { status: 'error'; message: string };

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

export default function AccessForm() {
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [company, setCompany] = useState('');
  const [website, setWebsite] = useState(''); // honeypot
  const [state, setState] = useState<FormState>({ status: 'idle' });

  async function onSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    const trimmed = {
      name: name.trim(),
      email: email.trim(),
      company: company.trim(),
      website: website.trim(),
    };
    if (!trimmed.name || !trimmed.company) {
      setState({ status: 'error', message: 'Please fill in every field.' });
      return;
    }
    if (!EMAIL_RE.test(trimmed.email)) {
      setState({ status: 'error', message: 'Please use a valid work email address.' });
      return;
    }
    setState({ status: 'submitting' });
    const result = await requestAccess(trimmed);
    // Only a real { ok: true } from the server confirms receipt — network
    // failures stay errors and can be retried; nothing is faked.
    if (result.ok) {
      setState({ status: 'success', email: trimmed.email });
    } else {
      setState({ status: 'error', message: result.error });
    }
  }

  if (state.status === 'success') {
    return (
      <div className="access-confirmation" role="status">
        <svg width="40" height="40" viewBox="0 0 40 40" aria-hidden="true">
          <circle cx="20" cy="20" r="19" fill="none" stroke="#2f9e6e" strokeWidth="2" />
          <path
            d="M12.5 20.5l5 5 10-11"
            fill="none"
            stroke="#2f9e6e"
            strokeWidth="2.5"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
        <h3>Request received</h3>
        <p>
          Thanks — we've received your request and will follow up at{' '}
          <strong>{state.email}</strong>.
        </p>
      </div>
    );
  }

  const submitting = state.status === 'submitting';

  return (
    <form className="access-form" onSubmit={onSubmit} noValidate>
      <div className="field">
        <label htmlFor="access-name">Name</label>
        <input
          id="access-name"
          name="name"
          type="text"
          autoComplete="name"
          required
          value={name}
          onChange={(e) => setName(e.target.value)}
          disabled={submitting}
        />
      </div>
      <div className="field">
        <label htmlFor="access-email">Work email</label>
        <input
          id="access-email"
          name="email"
          type="email"
          autoComplete="email"
          required
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          disabled={submitting}
        />
      </div>
      <div className="field">
        <label htmlFor="access-company">Company</label>
        <input
          id="access-company"
          name="company"
          type="text"
          autoComplete="organization"
          required
          value={company}
          onChange={(e) => setCompany(e.target.value)}
          disabled={submitting}
        />
      </div>
      {/* Honeypot: hidden from humans, catnip for bots. */}
      <div className="hp-field" aria-hidden="true">
        <label htmlFor="access-website">Website</label>
        <input
          id="access-website"
          name="website"
          type="text"
          tabIndex={-1}
          autoComplete="off"
          value={website}
          onChange={(e) => setWebsite(e.target.value)}
        />
      </div>
      {state.status === 'error' && (
        <p className="form-error" role="alert">
          {state.message}
        </p>
      )}
      <button className="btn btn-primary btn-block" type="submit" disabled={submitting}>
        {submitting ? 'Sending…' : 'Request access'}
      </button>
      <p className="form-note">We'll only use your details to follow up on your request.</p>
    </form>
  );
}
